import {
    CloseCircleOutlined,
    PrinterOutlined
} from '@ant-design/icons';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import type { TableColumnsType } from 'antd';
import {
    Button,
    message,
    Modal,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { formatTimeString } from 'utils/dateTimeUtils';
import { addOrdersToConsolidation } from '../../../services/consolidationService';
import PrintTagsModal from '../bags/components/PrintTagsModal';
import ConsolidationFilters, {
    ConsolidationFilterOptions,
    DEFAULT_FILTER_OPTIONS
} from './components/ConsolidationFilters';
import CreateConsolidationTaskDrawer from './components/CreateConsolidationTaskDrawer';
import MoveToConsolidationModal from './components/MoveToConsolidationModal';
import MoveToConsolidationResultModal from './components/MoveToConsolidationResultModal';
import NewBagModal from './components/NewBagModal';
import RemoveFromConsolidationModal from './components/RemoveFromConsolidationModal';
import WmsDownloadModal from '../../../components/WmsDownloadModal';
import TaskDetailDrawer from './components/TaskDetailDrawer';
import { useExecuteConsolidationAsync } from './hooks/useExecuteConsolidationAsync';
import {
    DISSOLVE_CONSOLIDATION_TASK,
    DissolveConsolidationTaskResponse,
    DissolveConsolidationTaskVariables
} from './graphql/mutations/dissolveConsolidationTask';
import {
    EXECUTE_CONSOLIDATION,
    ExecuteConsolidationResponse,
    ExecuteConsolidationVariables
} from './graphql/mutations/executeConsolidation';
import {
    EXPORT_CONSOLIDATION_TASKS,
    ExportConsolidationTasksResponse,
    ExportConsolidationTasksVariables
} from './graphql/mutations/exportConsolidationTasks';
import {
    useRemoveFromConsolidation
} from './graphql/mutations/removeFromConsolidation';
import {
    GET_CONSOLIDATION_TASK_DETAIL,
    GetConsolidationTaskDetailResponse,
    GetConsolidationTaskDetailVariables
} from './graphql/queries/getConsolidationTaskDetail';
import {
    GET_CONSOLIDATION_TASKS,
    GetConsolidationTasksResponse,
    GetConsolidationTasksVariables
} from './graphql/queries/getConsolidationTasks';
import {
    ConsolidationTaskStatus,
    ConsolidationTaskSummary,
    CreatedBagInfo,
    getStatusColor,
    getStatusText,
    BagType
} from './graphql/types/ConsolidationTypes';
import {useWarehouseContext} from "../../../contexts/WarehouseContext";

interface CreateTaskData {
    destinationHubId: string;
    destinationWhsId: string | null;
    startTime: string | null;
    endTime: string | null;
    estimatedParcelCount: number;
    estimatedBagCount: number;
    bagType: BagType;
    carrierCode?: number; // 新增Carrier代码字段
    isAllCarriers?: boolean; // 是否选择了ALL Carriers
    carrierTasks?: Array<{
        carrierCode: number;
        estimatedParcelCount: number;
        estimatedBagCount: number;
    }>; // 当选择ALL时，各个Carrier的任务数据
}

const { Text } = Typography;
const { confirm } = Modal;

const ConsolidationPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const client = useApolloClient();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [, setSelectedRows] = useState<ConsolidationTaskSummary[]>([]);
    const [filterOptions, setFilterOptions] = useState<ConsolidationFilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [tempFilterOptions, setTempFilterOptions] = useState<ConsolidationFilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    
    // 模态框和抽屉状态
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
    const [removeModalVisible, setRemoveModalVisible] = useState(false);
    const [moveModalVisible, setMoveModalVisible] = useState(false);
    
    // 新增弹窗及数据状态（与TaskDetailDrawer一致）
    const [newBagModalVisible, setNewBagModalVisible] = useState(false);
    const [newBagsList, setNewBagsList] = useState<CreatedBagInfo[]>([]);
    
    // 移入组包结果弹窗状态
    const [moveResultModalVisible, setMoveResultModalVisible] = useState(false);
    const [moveResults, setMoveResults] = useState<{
        totalCount: number;
        successCount: number;
        failedCount: number;
        failedTrackingNumbers: string[];
    } | null>(null);
    const [pendingNewBags, setPendingNewBags] = useState<CreatedBagInfo[]>([]); // 暂存新包牌数据
    
    // 打印包牌相关状态
    const [printModalVisible, setPrintModalVisible] = useState(false);
    const [selectedTaskBags, setSelectedTaskBags] = useState<any[]>([]);
    const [batchPrintLoading, setBatchPrintLoading] = useState(false);

    // 导出相关状态
    const [exportLoading, setExportLoading] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [downloadFileName, setDownloadFileName] = useState('');
    const [exportCooldown, setExportCooldown] = useState(false);

    // 防重复点击状态
    const [isOperating, setIsOperating] = useState(false);

    // GraphQL查询
    const { data, loading, error, refetch } = useQuery<
        GetConsolidationTasksResponse,
        GetConsolidationTasksVariables
    >(GET_CONSOLIDATION_TASKS, {
        variables: {
            pageRequest: {
                page: currentPage, // 现有PageRequestType页码从1开始
                size: pageSize
            },
            filters: {
                warehouseId: currentWarehouse?.id || '0',
                status: filterOptions.status !== 'all' ? filterOptions.status : undefined,
                taskNameSearch: filterOptions.taskNameSearch || undefined,
                hubName: filterOptions.hubName || undefined,
                whsName: filterOptions.whsName || undefined,
                carrierCode: filterOptions.carrierCode || undefined, // 新增Carrier筛选
                createdAtStart: filterOptions.createdAtRange[0]?.startOf('day').toISOString(),
                createdAtEnd: filterOptions.createdAtRange[1]?.endOf('day').toISOString(),
                type: 2 // Other Carrier类型
            }
        },
        fetchPolicy: 'cache-and-network'
    });
    // 解散组包任务 Mutation
    const [dissolveTask] = useMutation<
        DissolveConsolidationTaskResponse,
        DissolveConsolidationTaskVariables
    >(DISSOLVE_CONSOLIDATION_TASK, {
        onCompleted(data) {
            if (data.dissolveConsolidationTask.success) {
                message.success(data.dissolveConsolidationTask.message || t('wms.baggingTask.messages.bagTaskDisbandSuccess'));
                refetch();
            } else {
                message.error(data.dissolveConsolidationTask.message || t('wms.baggingTask.messages.bagTaskDisbandFailed'));
            }
        },
        onError(error) {
            message.error(`${t('wms.baggingTask.messages.bagTaskDisbandFailed')}: ${error.message}`);
        }
    });

    // 移出组包 Mutation
    const [removeFromConsolidation, { loading: removing }] = useRemoveFromConsolidation();

    // 同步执行组包任务 Mutation
    const [executeConsolidation, { loading: executeLoading }] = useMutation<
        ExecuteConsolidationResponse,
        ExecuteConsolidationVariables
    >(EXECUTE_CONSOLIDATION);

    // 导出组包任务 Mutation
    const [exportConsolidationTasks] = useMutation<
        ExportConsolidationTasksResponse,
        ExportConsolidationTasksVariables
    >(EXPORT_CONSOLIDATION_TASKS, {
        onCompleted(data) {
            const fileName = data.exportConsolidationTasks;
            setDownloadFileName(fileName);
            setShowDownloadModal(true);
            setExportLoading(false);
            setExportCooldown(true);
            setTimeout(() => setExportCooldown(false), 5000);
        },
        onError(error) {
            message.error(`${t('wms.baggingTask.messages.exportFailed')}: ${error.message}`);
            setExportLoading(false);
        }
    });

    // 异步执行组包任务
    const { executeAsync, loading: asyncExecuteLoading, isExecuting } = useExecuteConsolidationAsync({
        onSuccess: (taskLog) => {
            message.success(t('wms.baggingTask.messages.bagTaskExecutionSuccess'));
            refetch(); // 刷新任务列表
        },
        onError: (error) => {
            message.error(`${t('wms.baggingTask.messages.bagTaskExecutionFailed')}: ${error}`);
        }
    });

    // 处理错误
    useEffect(() => {
        if (error) {
            message.error(`${t('wms.baggingTask.messages.bagTaskLoadFailed')}: ${error.message}`);
        }
    }, [error, t]);

    // 监听筛选条件和分页参数变化，重新执行查询
    useEffect(() => {
        refetch();
    }, [filterOptions, currentPage, pageSize, refetch]);

    // 表格列定义
    const columns: TableColumnsType<ConsolidationTaskSummary> = [
        {
            title: t('wms.baggingTask.columns.taskNumber'),
            dataIndex: 'taskName',
            key: 'taskName',
            width: 200,
            fixed: 'left',
            align: 'left',
            render: (taskName: string, record: ConsolidationTaskSummary) => (
                <Button
                    type="link"
                    style={{
                        color: '#722ed1', // 紫色
                        padding: 0,
                        height: 'auto',
                        fontWeight: 500
                    }}
                    onClick={() => handleViewDetail(record.taskId)}
                >
                    {taskName}
                </Button>
            )
        },
        {
            title: t('wms.baggingTask.columns.carrier'),
            dataIndex: 'carrier',
            key: 'carrier',
            width: 150,
            align: 'left',
            render: (carrier: string) => carrier || '-',
        },
        {
            title: t('wms.baggingTask.columns.handoverHub'),
            dataIndex: 'hubName',
            key: 'hubName',
            width: 120,
            align: 'left',
            render: (hubName: string) => hubName || '-',
        },
        {
            title: t('wms.baggingTask.columns.handoverVehicle'),
            dataIndex: 'handoverVehicles',
            key: 'handoverVehicles',
            width: 150,
            align: 'left',
            render: (handoverVehicles: string) => handoverVehicles || '-',
        },
        {
            title: t('wms.baggingTask.columns.parcelCount'),
            dataIndex: 'parcelCount',
            key: 'parcelCount',
            width: 100,
            align: 'left',
            render: (count: number) => count || '-',
        },
        {
            title: t('wms.baggingTask.columns.bagCount'),
            dataIndex: 'bagCount',
            key: 'bagCount',
            width: 100,
            align: 'left',
            render: (count: number) => count || '-',
        },
        {
            title: t('wms.baggingTask.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            align: 'left',
            render: (status: ConsolidationTaskStatus) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusText(status)}
                </Tag>
            ),
        },
        {
            title: t('wms.baggingTask.columns.creationTime'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 160,
            align: 'left',
            render: (createdAt: string) => formatTimeString(createdAt).toString(),
        },
        {
            title: t('wms.baggingTask.columns.handoverTime'),
            dataIndex: 'handoverAt',
            key: 'handoverAt',
            width: 160,
            align: 'left',
            render: (handoverAt: string) => handoverAt ? formatTimeString(handoverAt).toString() : '-',
        },
        {
            title: t('wms.baggingTask.columns.action'),
            key: 'actions',
            width: 100,
            fixed: 'right',
            align: 'left',
            render: (_, record) => (
                <Space>
                    <Tooltip>
                <span>
                    <Button
                        type="link"
                        danger={record.isDeletable}
                        disabled={!record.isDeletable}
                        icon={
                            <CloseCircleOutlined
                                style={{ color: record.isDeletable ? undefined : '#d9d9d9' }}
                            />
                        }
                        onClick={record.isDeletable ? () => handleDelete(record) : undefined}
                    />
                </span>
                    </Tooltip>
                    <Button
                        type="link"
                        icon={<PrinterOutlined />}
                        onClick={() => handlePrintBags(record.taskId)}
                    >
                    </Button>
                </Space>
            ),
        },    ];

    // 事件处理函数
    const handleFilter = () => {
        setFilterOptions(tempFilterOptions);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setTempFilterOptions(DEFAULT_FILTER_OPTIONS);
        setFilterOptions(DEFAULT_FILTER_OPTIONS);
        setCurrentPage(1);
    };

    const handleCreateNew = () => {
        setCreateModalVisible(true);
    };

    const handleCreateSuccess = async (taskData: CreateTaskData) => {
        if (isOperating || isExecuting || executeLoading) return; // 防止重复操作
        setIsOperating(true);
        
        try {
            // 如果是批量创建（选择了ALL Carriers）
            if (taskData.isAllCarriers && taskData.carrierTasks && taskData.carrierTasks.length > 0) {
                console.log('批量创建组包任务:', taskData.carrierTasks);
                
                let successCount = 0;
                let failedCount = 0;
                const failedCarriers: string[] = [];
                
                // 遍历所有有值的Carrier，使用同步调用分别创建任务
                for (const carrierTask of taskData.carrierTasks) {
                    try {
                        const result = await executeConsolidation({
                            variables: {
                                input: {
                                    warehouseId: currentWarehouse?.id!,
                                    destinationHubId: taskData.destinationHubId,
                                    destinationWhsId: null,
                                    expectedDeliveryTimeStart: taskData.startTime || undefined,
                                    expectedDeliveryTimeEnd: taskData.endTime || undefined,
                                    bagType: taskData.bagType,
                                    carrier: carrierTask.carrierCode,
                                }
                            }
                        });
                        
                        if (result.data?.executeConsolidation.success) {
                            successCount++;
                            console.log(`Carrier ${carrierTask.carrierCode} 任务创建成功`);
                        } else {
                            failedCount++;
                            failedCarriers.push(`Carrier ${carrierTask.carrierCode}`);
                            console.error(`Carrier ${carrierTask.carrierCode} 任务创建失败:`, result.data?.executeConsolidation.message);
                        }
                    } catch (error: any) {
                        failedCount++;
                        failedCarriers.push(`Carrier ${carrierTask.carrierCode}`);
                        console.error(`Carrier ${carrierTask.carrierCode} 任务创建失败:`, error);
                    }
                }
                
                // 显示批量创建结果
                if (failedCount === 0) {
                    message.success(t('wms.baggingTask.messages.successfullyCreatedTasks', { count: successCount }));
                } else if (successCount === 0) {
                    message.error(t('wms.baggingTask.messages.failedToCreateAllTasks', { count: failedCount, carriers: failedCarriers.join(', ') }));
                } else {
                    message.warning(t('wms.baggingTask.messages.partiallyCreatedTasks', { successCount, failedCount, carriers: failedCarriers.join(', ') }));
                }
                
                // 刷新任务列表
                refetch();
                setCreateModalVisible(false);
            } else {
                // 单个Carrier的创建逻辑（使用异步调用）
                await executeAsync({
                    input: {
                        warehouseId: currentWarehouse?.id!,
                        destinationHubId: taskData.destinationHubId,
                        destinationWhsId: taskData.destinationWhsId, // 可以为null，表示All
                        expectedDeliveryTimeStart: taskData.startTime || undefined,
                        expectedDeliveryTimeEnd: taskData.endTime || undefined,
                        bagType: taskData.bagType,
                        carrier: taskData.carrierCode || 0, // 使用carrierCode，如果没有则默认为0（SWIFTX）
                    }
                });
                setCreateModalVisible(false);
            }
        } catch (error) {
            message.error(t('wms.baggingTask.messages.baggingTaskCreationFailed'));
        } finally {
            setIsOperating(false);
        }
    };

    const handleViewDetail = (taskId: string) => {
        setSelectedTaskId(taskId);
        setDetailDrawerVisible(true);
    };

    const handleEdit = (taskId: string) => {
        message.info(`编辑任务: ${taskId} (功能开发中)`);
    };

    // 打印包牌处理函数
    const handlePrintBags = async (taskId: string) => {
        try {
            const { data } = await client.query<
                GetConsolidationTaskDetailResponse,
                GetConsolidationTaskDetailVariables
            >({
                query: GET_CONSOLIDATION_TASK_DETAIL,
                variables: {
                    taskId,
                    warehouseId: currentWarehouse?.id || '0'
                },
                fetchPolicy: 'network-only'
            });
            
            if (data?.getConsolidationTaskDetails?.bags) {
                const bags = data.getConsolidationTaskDetails.bags.map(bag => ({
                    bagId: bag.bagId,
                    bagNumber: bag.bagNumber,
                    bagType: 1, // 默认包牌类型
                    warehouseId: null,
                    sortingCode: bag.bagCode,
                    status: 1,
                    createdAt: null,
                    id: bag.bagId
                }));
                setSelectedTaskBags(bags);
                setPrintModalVisible(true);
            } else {
                message.warning(t('wms.baggingTask.messages.noBagsToPrint'));
            }
        } catch (error) {
            message.error(t('wms.baggingTask.messages.failedToGetBagsInfo'));
        }
    };

    // 导出包牌处理函数
    const handleExportTasks = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('wms.baggingTask.messages.pleaseSelectTasksToExport'));
            return;
        }
        if (exportCooldown) {
            return;
        }

        setExportLoading(true);
        try {
            await exportConsolidationTasks({
                variables: {
                    taskIds: selectedRowKeys.map(id => String(id))
                }
            });
        } catch (error) {
            console.error('Export error:', error);
        }
    };

    // 批量打印包牌处理函数
    const handleBatchPrintBags = async () => {
        setBatchPrintLoading(true);

        try {
            // 1. 获取选中的任务
            const selectedTasks = tasks.filter(task =>
                selectedRowKeys.includes(task.taskId)
            );
            
            // 2. 检查是否包含已解散任务
            const hasDissolvedTasks = selectedTasks.some(task => 
                task.status === ConsolidationTaskStatus.DISSOLVED
            );
            
            if (hasDissolvedTasks) {
                message.error(t('wms.baggingTask.messages.cannotPrintTagsForDissolvedTasks'));
                setBatchPrintLoading(false);
                return;
            }
            
            // 3. 并行查询所有任务的包牌详情
            const bagDetailsPromises = selectedTasks.map(task => 
                client.query<GetConsolidationTaskDetailResponse, GetConsolidationTaskDetailVariables>({
                    query: GET_CONSOLIDATION_TASK_DETAIL,
                    variables: {
                        taskId: task.taskId,
                        warehouseId: currentWarehouse?.id || '0'
                    },
                    fetchPolicy: 'network-only'
                })
            );
            
            const bagDetailsResults = await Promise.allSettled(bagDetailsPromises);
            
            // 4. 提取成功的结果
            const successfulResults = bagDetailsResults
                .filter((result): result is PromiseFulfilledResult<any> => 
                    result.status === 'fulfilled'
                )
                .map(result => result.value);
            
            // 5. 合并所有包牌数据到统一格式
            const allBags = successfulResults
                .flatMap(result => result.data?.getConsolidationTaskDetails?.bags || [])
                .map(bag => ({
                    bagId: bag.bagId,
                    bagNumber: bag.bagNumber,
                    bagType: 1,
                    warehouseId: null,
                    sortingCode: bag.bagCode,
                    status: 1,
                    createdAt: null,
                    id: bag.bagId
                }));
            
            // 6. 去重处理（防止重复包牌）
            const uniqueBags = allBags.filter((bag, index, self) => 
                self.findIndex(b => b.bagId === bag.bagId) === index
            );
            
            // 7. 统一显示到同一个Modal中
            if (uniqueBags.length > 0) {
                setSelectedTaskBags(uniqueBags);
                setPrintModalVisible(true);
            }
            
        } catch (error) {
            // 静默处理错误，不显示任何提示
            console.error('Batch print error:', error);
        } finally {
            setBatchPrintLoading(false);
        }
    };

    const handleDelete = (record: ConsolidationTaskSummary) => {
        confirm({
            title: t('wms.baggingTask.messages.confirmDisbandingTask'),
            content: t('wms.baggingTask.messages.confirmDisbandingTaskContent', { taskName: record.taskName }),
            okText: t('components.button.confirm'),
            cancelText: t('components.button.cancel'),
            type: 'warning',
            onOk: () => {
                dissolveTask({ variables: { taskId: record.taskId } });
            },
            onCancel: () => {},
        });
    };

    const handleBatchOperation = (operation: string) => {
        if (selectedRowKeys.length === 0) {
            message.warning('请先选择要操作的任务');
            return;
        }
        message.info(`批量${operation}功能开发中`);
    };

    const handleRemoveFromConsolidation = async (trackingNumbers: string[]) => {
        if (isOperating) return; // 防止重复操作
        setIsOperating(true);
        
        try {
            const { data } = await removeFromConsolidation({
                variables: {
                    input: {
                        warehouseId: currentWarehouse?.id!,
                        trackingNumbers
                    }
                }
            });

            if (data?.removeOrdersFromConsolidation.success) {
                message.success(data.removeOrdersFromConsolidation.message);
                setRemoveModalVisible(false); // 成功后关闭弹窗
                refetch(); // 刷新列表
            } else {
                message.error(data?.removeOrdersFromConsolidation.message || t('wms.baggingTask.messages.failedToRemoveFromConsolidation'));
                // 失败时不关闭弹窗，让用户可以重试或查看错误
            }
        } catch (error: any) {
            message.error(t('wms.baggingTask.messages.failedToRemoveFromConsolidation') + ': ' + (error.message || t('wms.baggingTask.messages.unknownError')));
            // 网络错误等异常情况也不关闭弹窗，让用户可以重试
        } finally {
            setIsOperating(false);
        }
    };

    // 修改移入组包方法，显示详细的处理结果
    const handleMoveToConsolidation = async (taskId: string, trackingNumbers: string[]) => {
        if (isOperating) return; // 防止重复操作
        setIsOperating(true);
        
        try {
            if (!taskId || !trackingNumbers || trackingNumbers.length === 0) {
                message.warning(t('wms.baggingTask.messages.missingMoveInformation'));
                return; // 不关闭弹窗
            }

            const response = await addOrdersToConsolidation(currentWarehouse?.id!, taskId, trackingNumbers);
            const { newBags, results, message: msg } = response;
            
            // 1. 关闭输入弹窗
            setMoveModalVisible(false);
            
            // 2. 暂存新包牌数据，稍后显示
            setPendingNewBags(newBags);
            
            // 3. 显示处理结果统计弹窗
            setMoveResults(results);
            setMoveResultModalVisible(true);
            
            // // 4. 根据结果显示相应的全局提示
            // if (results.failedCount > 0) {
            //     message.warning(`Processing completed: ${results.successCount} successful, ${results.failedCount} failed`);
            // } else {
            //     message.success(`Move to consolidation completed: ${results.successCount} orders processed successfully`);
            // }
            
            // 5. 刷新列表
            refetch();
            
        } catch (error) {
            message.error(t('wms.baggingTask.messages.failedToMoveToConsolidation'));
            console.error('Move to consolidation error:', error);
        } finally {
            setIsOperating(false);
        }
    };

    // 处理结果弹窗的OK按钮回调
    const handleShowNewBags = () => {
        if (pendingNewBags.length > 0) {
            setNewBagsList(pendingNewBags);
            setNewBagModalVisible(true);
            setPendingNewBags([]); // 清空暂存数据
        }
    };

    // 处理结果弹窗关闭回调
    const handleResultModalClose = () => {
        setMoveResultModalVisible(false);
        setMoveResults(null);
        // 如果有待显示的新包牌，也触发显示
        if (pendingNewBags.length > 0) {
            handleShowNewBags();
        }
    };

    const handleTableChange = (pagination: any) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys: React.Key[], rows: ConsolidationTaskSummary[]) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
        },
    };

    const tasks = data?.getConsolidationTasks.tasks || [];
    const pagination = data?.getConsolidationTasks.pagination;

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* 过滤器 */}
                <ConsolidationFilters
                    loading={loading}
                    pageType="other-carrier"
                    filters={tempFilterOptions}
                    setFilters={setTempFilterOptions}
                />

                {/* 批量操作区域 */}
                <div className="flex justify-between items-center mb-3 pl-0">
                    <div className="flex gap-4">
                        <Button
                            type="primary"
                            onClick={handleCreateNew}
                            disabled={isOperating || isExecuting}
                            loading={isExecuting}
                        className="w-auto min-w-24"
                        >
                        <span className="font-bold">{isExecuting ? t('wms.baggingTask.buttons.executing') : t('wms.baggingTask.buttons.newBaggingTask')}</span>
                        </Button>
                        <Button
                            type="primary"
                            style={{ background: '#722ED1', borderColor: '#722ED1' }}
                            onClick={() => setMoveModalVisible(true)}
                            disabled={isOperating}
                        className="w-auto min-w-24"
                        >
                        <span className="font-bold">{t('wms.baggingTask.buttons.moveIntoTask')}</span>
                        </Button>
                        <Button
                            type="primary"
                            style={{ background: '#722ED1', borderColor: '#722ED1' }}
                            onClick={() => setRemoveModalVisible(true)}
                            disabled={isOperating}
                        className="w-auto min-w-24"
                        >
                        <span className="font-bold">{t('wms.baggingTask.buttons.moveOutOfTask')}</span>
                        </Button>
                        <Button
                            type="primary"
                            style={{
                                background: selectedRowKeys.length > 0 ? '#722ED1' : undefined,
                                borderColor: selectedRowKeys.length > 0 ? '#722ED1' : undefined
                            }}
                            onClick={handleBatchPrintBags}
                            disabled={selectedRowKeys.length === 0}
                            loading={batchPrintLoading}
                        className="w-auto min-w-24"
                        >
                        <span className="font-bold">{t('wms.baggingTask.buttons.printTags')} { selectedRowKeys.length > 0 }</span>
                        </Button>
                        <Button
                            type="primary"
                            style={{
                                background: selectedRowKeys.length > 0 ? '#52c41a' : undefined,
                                borderColor: selectedRowKeys.length > 0 ? '#52c41a' : undefined
                            }}
                            onClick={handleExportTasks}
                            disabled={selectedRowKeys.length === 0 || exportCooldown}
                            loading={exportLoading}
                        className="w-auto min-w-24"
                        >
                        <span className="font-bold">{t('wms.baggingTask.buttons.export')}</span>
                        </Button>
                    </div>
                    <div className="flex gap-4">
                        <Button
                            type="primary"
                            onClick={handleFilter}
                            loading={loading}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('components.button.filter')}</span>
                        </Button>
                        <Button
                            onClick={handleResetFilters}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('components.button.reset')}</span>
                        </Button>
                    </div>
                </div>

                {/* 任务列表表格 */}
                <div className="[&_.ant-table-body]:overflow-scroll [&_.ant-table-body]:overflow-x-scroll [&_.ant-table-body]:overflow-y-scroll">
                    <Table
                        columns={columns}
                        dataSource={tasks}
                        rowKey="taskId"
                        loading={loading}
                        rowSelection={rowSelection}
                        scroll={{ x: 1200 }}
                        pagination={false}
                        onChange={handleTableChange}
                     className="mb-4"/>
                </div>
                {/* 分页 */}
                <TablePaginationBar
                    total={pagination?.totalElements || 0}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={(page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    }}
                    pageSizeOptions={['10', '20', '50', '100']}
                />

                {/* 新建组包抽屉 */}
                <CreateConsolidationTaskDrawer
                    visible={createModalVisible}
                    onClose={() => setCreateModalVisible(false)}
                    onSubmit={handleCreateSuccess}
                    isExecuting={isExecuting}
                    pageType="other-carrier"
                />

                {/* 移出组包弹窗 */}
                <RemoveFromConsolidationModal
                    visible={removeModalVisible}
                    disabled={isOperating}
                    onOk={handleRemoveFromConsolidation}
                    onCancel={() => setRemoveModalVisible(false)}
                />

                {/* 移入组包弹窗 */}
                <MoveToConsolidationModal
                    visible={moveModalVisible}
                    defaultTaskId={''}
                    disabled={isOperating}
                    type={2}
                    onOk={handleMoveToConsolidation}
                    onCancel={() => setMoveModalVisible(false)}
                />
                {/* 新包牌弹窗 */}
                <NewBagModal
                    visible={newBagModalVisible}
                    newBags={newBagsList}
                    onPrint={() => {
                        if (newBagsList.length > 0) {
                            // 直接使用newBagsList，转换为PrintTagsModal需要的格式
                            const bags = newBagsList.map(bag => ({
                                bagId: bag.bagId,
                                bagNumber: bag.bagNumber,
                                id: bag.bagId,
                                bagType: 1,
                                warehouseId: null,
                                sortingCode: bag.bagCode,
                                status: 1,
                                createdAt: null,
                            }));
                            setSelectedTaskBags(bags);
                            setPrintModalVisible(true);
                            setNewBagModalVisible(false);
                        } else {
                            message.warning(t('wms.baggingTask.messages.noBagsToPrint'));
                        }
                    }}
                    onOk={() => setNewBagModalVisible(false)}
                />

                {/* 移入组包结果弹窗 */}
                <MoveToConsolidationResultModal
                    visible={moveResultModalVisible}
                    results={moveResults}
                    onClose={handleResultModalClose}
                    onShowNewBags={handleShowNewBags}
                />

                {/* 任务详情抽屉 */}
                <TaskDetailDrawer
                    visible={detailDrawerVisible}
                    taskId={selectedTaskId}
                    isOperating={isOperating}
                    type={2}
                    onClose={() => {
                        setDetailDrawerVisible(false);
                        setSelectedTaskId(null);
                    }}
                    onEdit={handleEdit}
                    onDelete={(taskId) => {
                        const task = tasks.find(t => t.taskId === taskId);
                        if (task) {
                            handleDelete(task);
                        }
                    }}
                />

                {/* 打印包牌弹窗 */}
                <PrintTagsModal
                    visible={printModalVisible}
                    onClose={() => setPrintModalVisible(false)}
                    bags={selectedTaskBags}
                    initialPrintCopies={1}
                />

                {/* 下载中心弹窗 */}
                <WmsDownloadModal
                    visible={showDownloadModal}
                    onClose={() => setShowDownloadModal(false)}
                    fileName={downloadFileName}
                />
        </div>
    );
};

export default ConsolidationPage;